/**
 * 网格辅助函数。
 *
 * 地图被划分为 gridSize 单位的正方形格子。塔会吸附到格子中心，
 * 当玩家进入放置模式时渲染器会绘制网格。
 *
 * 像素（= 地图）坐标约定：
 *   - 格子 (0, 0) 覆盖左上角 `gridSize × gridSize` 区域。
 *   - `pixelToGrid` 返回包含给定像素的格子索引。
 *   - `gridCellCentre` 返回格子中心的像素坐标。
 *   - `snapToGridCentre` 将原始指针坐标吸附到最近格子的中心。
 *
 * 所有坐标都在 Phaser 逻辑地图空间内（得益于 `scale: NONE`，与 CSS 像素 1:1）。
 */

export interface GridBounds {
  /** 列数（0-based，含最大值） */
  cols: number;
  /** 行数（0-based，含最大值） */
  rows: number;
  gridSize: number;
}

/** 将像素坐标转换为所在单元格的索引（向下取整）。 */
export function pixelToGrid(px: number, py: number, gridSize: number): { gx: number; gy: number } {
  return {
    gx: Math.floor(px / gridSize),
    gy: Math.floor(py / gridSize),
  };
}

/** 格子 (gx, gy) 的像素中心坐标。 */
export function gridCellCentre(gx: number, gy: number, gridSize: number): { x: number; y: number } {
  return {
    x: gx * gridSize + gridSize / 2,
    y: gy * gridSize + gridSize / 2,
  };
}

/** 将原始指针坐标吸附到其所在单元格的中心。 */
export function snapToGridCentre(
  px: number,
  py: number,
  gridSize: number,
): { x: number; y: number } {
  const { gx, gy } = pixelToGrid(px, py, gridSize);
  return gridCellCentre(gx, gy, gridSize);
}

/**
 * 可建造的格子：在地图矩形内部（排除会溢出的最后一列/行）且
 * 未被已有塔占用。
 *
 * 当 (gx, gy) 是有效放置格子时返回 true。
 */
export function isGridCellBuildable(
  gx: number,
  gy: number,
  bounds: GridBounds,
  occupied: Set<string>,
): boolean {
  if (gx < 0 || gy < 0) return false;
  if (gx > bounds.cols || gy > bounds.rows) return false;
  return !occupied.has(`${gx},${gy}`);
}

/**
 * 从塔状态数组填充占用格子集合。
 * 每个塔都会吸附到其所在格子，即使两座塔位置相近但未完全重合于同一中心也能检测到重叠。
 */
export function occupiedGridCells(
  towers: { x: number; y: number }[],
  gridSize: number,
): Set<string> {
  const set = new Set<string>();
  for (const t of towers) {
    const { gx, gy } = pixelToGrid(t.x, t.y, gridSize);
    set.add(`${gx},${gy}`);
  }
  return set;
}
